import * as Muicon from '@mui/icons-material'
import CancelIcon from '@mui/icons-material/Cancel'
import LockIcon from '@mui/icons-material/Lock'
import { Box, IconButton, Modal, Paper, Typography } from '@mui/material'
import ReactMarkdown from 'react-markdown'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { login } from '../../../api/auth'
import { translateText } from '../../../language'
import { useThemeMode } from '../../../theme'
import { isDefined, isTokenValid } from '../../../utils'
import { resolveLink } from '../../../utils/resolveLink'
import { Button } from '../widgets'
import * as CustomIcons from '../widgets'

export const NotificationModal = ({ notification, open, onClose }) => {
  const { palette } = useThemeMode()
  const { i18n, t } = useTranslation()
  const profile = useSelector((state) => state.auth?.profile)
  const authToken = useSelector((state) => state.auth?.authToken)
  const config = useSelector((state) => state.config)
  const language = useSelector((state) => state.language)

  if (!notification) return null

  const title = translateText(notification.titles ?? [], i18n)
  const description = translateText(notification.descriptions ?? [], i18n)
  const iconName = notification.icon ?? 'StarOutlined'
  const Icon = CustomIcons[iconName] ?? Muicon[iconName] ?? Muicon.StarOutlined
  const link = notification.link
  const linkFallback = notification.link_fallback
  const votingStartUtc = notification?.deadline_metadata?.voting_start_utc
  const linkDisabled = isDefined(votingStartUtc) && new Date(votingStartUtc) > new Date()

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box sx={{ minWidth: '60%', maxWidth: '85%', position: 'relative', outline: 'none' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            width: '28px',
            height: '28px',
            right: '10px',
            top: '10px',
            zIndex: 1,
            color: palette.paperColor,
            background: palette.headerColor,
            '&:hover': { background: palette.headerBrightColor }
          }}
        >
          <CancelIcon fontSize="small" />
        </IconButton>

        <Paper>
          <Box
            sx={{
              background: palette.headerColor,
              padding: '10px 40px 10px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <Icon sx={{ color: palette.paperColor, fontSize: '1.6rem', opacity: 0.9 }} />
            <Typography fontWeight={600} color={palette.paperColor} fontSize="0.95rem">
              {title}
            </Typography>
          </Box>

          {description && (
            <Box
              sx={{
                padding: '10px',
                maxHeight: '300px',
                overflowY: 'auto',
                color: palette.textColor,
                fontSize: '0.9rem',
                lineHeight: 1.6,
                '& p': { margin: '0 0 8px 0' },
                '& p:last-child': { margin: 0 },
                '& strong': { color: palette.headerColor }
              }}
            >
              <ReactMarkdown>{description}</ReactMarkdown>
            </Box>
          )}

          {!linkDisabled && (link || notification.require_auth) && (
            <Box sx={{ padding: '8px 10px', textAlign: 'right', borderTop: `1px solid ${palette.paperColorDivider}` }}>
              {notification.require_auth && !(isTokenValid(authToken) && isDefined(profile))
                ? (
                  <Button
                    startIcon={<LockIcon fontSize="small" />}
                    onClick={() => { onClose(); login(config.config, language.active) }}
                  >
                    {t('notification-login-required')}
                  </Button>
                  )
                : link && (
                  <Button
                    variant="contained"
                    onClick={async () => {
                      const resolved = await resolveLink(link, linkFallback)
                      if (resolved) {
                        onClose()
                        if (resolved.startsWith('/')) {
                          window.location.href = resolved
                        } else {
                          window.open(resolved, '_blank', 'noopener,noreferrer')
                        }
                      }
                    }}
                  >
                    {t('Open')}
                  </Button>
                )}
            </Box>
          )}
        </Paper>
      </Box>
    </Modal>
  )
}
